import Stripe from 'stripe'
import { useCallback, useState } from 'react'

export type ValidatedCard = {
  token: string
  last4: string
  brand: string
}

export type RawCardInput = {
  number: string
  expMonth: number
  expYear: number
  cvc: string
  name: string
  addressZip?: string
}

export type ValidateResult = { ok: true; card: ValidatedCard } | { ok: false; error: string }

type UseStripeCardResult = {
  errorMessage: string | null
  isValidating: boolean
  validate: (input: RawCardInput) => Promise<ValidateResult>
  reset: () => void
}

const parseExp = (mmYy: string): { month: number; year: number } | null => {
  const trimmed = mmYy.trim()
  const match = /^(\d{1,2})\s*\/\s*(\d{2}|\d{4})$/.exec(trimmed)
  if (!match) return null
  const month = Number.parseInt(match[1], 10)
  let year = Number.parseInt(match[2], 10)
  if (year < 100) year += 2000
  if (month < 1 || month > 12) return null
  return { month, year }
}

export const parseExpiration = parseExp

export const useStripeCard = (stripeKey: string): UseStripeCardResult => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  const validate = useCallback(
    async (input: RawCardInput): Promise<ValidateResult> => {
      if (!stripeKey) {
        const message = 'Stripe is not configured'
        setErrorMessage(message)
        const failure: ValidateResult = { ok: false, error: message }
        return failure
      }
      setIsValidating(true)
      setErrorMessage(null)
      try {
        const stripe = new Stripe(stripeKey)
        const card = {
          number: input.number,
          exp_month: String(input.expMonth),
          exp_year: String(input.expYear),
          cvc: input.cvc,
          name: input.name,
          ...(input.addressZip ? { address_zip: input.addressZip } : {}),
        }
        const token = await stripe.tokens.create({ card })
        return {
          ok: true,
          card: {
            token: token.id,
            last4: token.card?.last4 ?? '',
            brand: token.card?.brand ?? '',
          },
        }
      } catch (error: unknown) {
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Card tokenization failed')
            : 'Card tokenization failed'
        setErrorMessage(message)
        return { ok: false, error: message }
      } finally {
        setIsValidating(false)
      }
    },
    [stripeKey],
  )

  const reset = useCallback(() => {
    setErrorMessage(null)
  }, [])

  return { errorMessage, isValidating, validate, reset }
}
